import { PMJSONNode } from "@/bundles/types/LessonSparkAttachment";

export interface BodySection {
  heading: string;
  contentNodes: PMJSONNode[];
}

export interface ParsedBody {
  preamble: PMJSONNode[];
  sections: BodySection[];
}

/**
 * Wraps a flat list of ProseMirror nodes into a valid doc node.
 * Falls back to an empty paragraph if nodes is empty to satisfy the schema.
 */
export function createDocFromNodes(nodes: PMJSONNode[]): PMJSONNode {
  return {
    type: "doc",
    content: nodes.length > 0 ? nodes : [{ type: "paragraph", content: [] }],
  };
}

/**
 * Splits a ProseMirror doc's content into a preamble and collapsible sections
 * for Lesson Sparks.
 * Sections are delimited by level-2 headings; any nodes before the first
 * heading are collected into `preamble` and rendered outside the sections.
 * Level-3 headings within a section are treated as styled subheadings.
 */
function parseBodyIntoSections(rawBody: PMJSONNode): ParsedBody {
  const content = rawBody.content ?? [];
  const preamble: PMJSONNode[] = [];
  const sections: BodySection[] = [];
  let currentSection: BodySection | null = null;

  for (const node of content) {
    if (node.type === "headings_block" && node.attrs?.["level"] === 2) {
      if (currentSection) sections.push(currentSection);
      const headingText = node.content?.map((n) => n.text ?? "").join("") ?? "";
      currentSection = { heading: headingText, contentNodes: [] };
    } else if (currentSection) {
      currentSection.contentNodes.push(node);
    } else {
      preamble.push(node);
    }
  }

  if (currentSection) sections.push(currentSection);
  return { preamble, sections };
}

export default parseBodyIntoSections;
